import { useQuery } from "@tanstack/vue-query";
import { request } from "@/shared/http/client";

export interface ChannelFunnel {
	readonly ruleId: string;
	readonly ruleName: string;
	readonly pipelineCode: string;
	readonly pipelineName: string;
	readonly event: string;
	readonly isActive: boolean;
	readonly stage: string | null;
	readonly stageName: string | null;
}

export interface PipelineOption {
	readonly code: string;
	readonly name: string;
	readonly entityCode: string;
	readonly holdsContacts: boolean;
	readonly stages: ReadonlyArray<{ readonly code: string; readonly name: string }>;
}

interface Rule {
	readonly id: string;
	readonly name: string;
	readonly sourceKind: string;
	readonly sourceCode: string;
	readonly event: string;

	readonly entry: { pipeline: string; stage: string | null } | null;
	readonly isActive: boolean;
	readonly condition?: Record<string, unknown>;
}

export interface ChannelFunnelIndex {
	linksOf(channelId: string): ChannelFunnel[];
	readonly pipelines: number;
	readonly options: readonly PipelineOption[];
}

interface PipelineRow {
	readonly code: string;
	readonly name: string;
	readonly entityCode?: string;
	readonly holdsContacts?: boolean;
	readonly stages?: ReadonlyArray<{ readonly code: string; readonly name: string }>;
}

export function useChannelFunnels() {
	return useQuery({
		queryKey: ["channels", "funnels"] as const,
		staleTime: 60_000,
		queryFn: async (): Promise<ChannelFunnelIndex> => {
			const [rules, pipelines] = await Promise.all([
				request<{ rules: Rule[] }>("/v3/automation/rules"),
				request<{ pipelines: PipelineRow[] }>("/v3/board/pipelines"),
			]);
			const names = new Map(pipelines.pipelines.map((p) => [p.code, p.name]));
			const options: PipelineOption[] = pipelines.pipelines.map((p) => ({
				code: p.code,
				name: p.name,
				entityCode: p.entityCode ?? "",
				holdsContacts: p.holdsContacts === true,
				stages: p.stages ?? [],
			}));
			const stageNames = new Map(
				options.flatMap((p) => p.stages.map((s) => [`${p.code}/${s.code}`, s.name] as const)),
			);

			const bySource = new Map<string, ChannelFunnel[]>();
			for (const rule of rules.rules) {
				if (rule.sourceKind !== "channel") continue;
				for (const landing of landings(rule)) {
					const found = bySource.get(rule.sourceCode) ?? [];
					found.push({
						ruleId: rule.id,
						ruleName: rule.name,
						pipelineCode: landing.pipeline,
						pipelineName: names.get(landing.pipeline) ?? landing.pipeline,
						event: rule.event,
						isActive: rule.isActive,
						stage: landing.stage,
						stageName: landing.stage
							? (stageNames.get(`${landing.pipeline}/${landing.stage}`) ?? landing.stage)
							: null,
					});
					bySource.set(rule.sourceCode, found);
				}
			}
			return {
				linksOf: (channelId: string) => bySource.get(channelId) ?? [],
				pipelines: pipelines.pipelines.length,
				options,
			};
		},
	});
}

function landings(rule: Rule): Array<{ pipeline: string; stage: string | null }> {
	return rule.entry ? [rule.entry] : [];
}
